import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer, Button, Skeleton, Modal, Input, Select, ErrorBoundary, FileUpload, useToast } from '@bbpos/ui';
import { docCurrencySymbol, formatDocAmount, toTenantAmount } from '@/utils/purchase-currency';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import { GstTaxCell, PlaceOfSupply, isGstSplit } from '@/features/purchase-orders';
import {
  useVendorBill, useRecordVendorPayment, useVoidVendorPayment, useCancelVendorBill, usePostVendorBill, useAddVendorBillDocument, useDownloadVendorBillPdf, VendorBillStatusBadge,
  uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, formatBytes,
} from '@/features/vendor-bills';
import type { VendorPaymentMethod } from '@/features/vendor-bills';
import { useAuthStore } from '@/stores/use-auth-store';
import { storeTimezone, todayIn } from '@/lib/store-clock';
import { ROLE_LEVELS } from '@/types/user';
import { BanIcon, CheckIcon, DownloadIcon } from '../purchase-orders/icons';

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const AlertTriangleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

const METHOD_OPTIONS:{ value: VendorPaymentMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' }, { value: 'UPI', label: 'UPI' }, { value: 'CASH', label: 'Cash (from drawer)' },
  { value: 'CHEQUE', label: 'Cheque' }, { value: 'CARD', label: 'Card' },
];

function fmtDate(v: string | null | undefined) {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

function VendorBillDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const { data: bill, isLoading, error } = useVendorBill(id);
  // A bill is in its PO's currency; payments on a foreign one also record the
  // tenant currency actually paid (docs/multi-currency-purchasing-plan.md D5).
  const currency = bill?.currency_code ?? tenantCurrency;
  const isForeign = !!bill?.currency_code;
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const tenantSubunit = getSubunitMultiplier(tenantCurrency);
  const tenantDp = getCurrencyDecimals(tenantCurrency);
  const [payBase, setPayBase] = useState('');
  const [payBaseTouched, setPayBaseTouched] = useState(false);
  const payMutation = useRecordVendorPayment();
  const cancelMutation = useCancelVendorBill();
  const postMutation = usePostVendorBill();
  const docMutation = useAddVendorBillDocument();
  const pdfMutation = useDownloadVendorBillPdf();

  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<VendorPaymentMethod>('BANK_TRANSFER');
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payRef, setPayRef] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const voidMutation = useVoidVendorPayment();
  const [voidPaymentId, setVoidPaymentId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [showPost, setShowPost] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!error) return;
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/vendor-bills');
  }, [error, toast, navigate]);

  if (error) return null;
  if (isLoading || !bill) return (<><Skeleton variant="rect" height={96} className="rounded-xl mb-6" /><Skeleton variant="rect" height={320} className="rounded-xl" /></>);

  const isDraft = bill.status === 'DRAFT';
  const payable = (bill.status === 'OPEN' || bill.status === 'PARTIALLY_PAID') && isManager;
  // Any manager may discard a draft; cancelling a posted bill needs the owner.
  // A debit note set off on the bill holds it like a payment (QA PU3-005).
  const cancellable = bill.status !== 'CANCELLED' && bill.amount_paid === 0 && !(bill.credited_amount ?? 0) && (isDraft ? isManager : isOwner);
  // A bill in a supplier currency voids its latest payment first (QA PU4-005, plan G7).
  const latestLivePaymentId = bill.payments.filter((p) => !p.voided_at)
    .reduce<{ id: string; created_at: string } | null>((a, p) => (!a || p.created_at >= a.created_at ? p : a), null)?.id;
  // Past due = an unpaid posted bill whose due day is before the store's today (QA PU3-003).
  const overdue = (bill.status === 'OPEN' || bill.status === 'PARTIALLY_PAID') && bill.due_date
    && bill.due_date.slice(0, 10) < todayIn(storeTimezone(profile, bill.location_id));

  function handleDownloadPdf() {
    if (!bill) return;
    pdfMutation.mutate(bill.id, {
      onSuccess: ({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename ?? `${bill.bill_number}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      onError: (e: Error) => toast({ title: e.message || 'Failed to download the bill', variant: 'error' }),
    });
  }
  function handlePost() {
    postMutation.mutate({ id: bill!.id, confirmDuplicate }, {
      onSuccess: (b) => { setShowPost(false); toast({ title: `${b.bill_number} posted — ${formatDocAmount(b.balance_due, currency)} due ${fmtDate(b.due_date)}`, variant: 'success' }); },
      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
    });
  }
  function openPay() { setPayAmount((bill!.balance_due / subunit).toFixed(dp)); setPayMethod('BANK_TRANSFER'); setPayRef(''); setPayBase(''); setPayBaseTouched(false); setShowPay(true); }
  // What this amount is worth in the tenant currency at the PO rate: the
  // default for "Paid in …"; the user types what the bank or till actually paid.
  const payBaseSuggested = isForeign && bill?.exchange_rate
    ? toTenantAmount(Math.round((parseFloat(payAmount) || 0) * subunit), currency, tenantCurrency, String(Number(bill.exchange_rate)))
    : null;
  const payBaseShown = payBaseTouched ? payBase : payBaseSuggested != null ? (payBaseSuggested / tenantSubunit).toFixed(tenantDp) : '';
  function handlePay() {
    const amount = Math.round((parseFloat(payAmount) || 0) * subunit);
    if (amount <= 0 || amount > bill!.balance_due) { toast({ title: `Amount must be between ${formatDocAmount(1, currency)} and the balance due`, variant: 'error' }); return; }
    const baseAmount = isForeign ? Math.round((parseFloat(payBaseShown) || 0) * tenantSubunit) : undefined;
    if (isForeign && (!baseAmount || baseAmount <= 0)) { toast({ title: `Enter the amount paid in ${tenantCurrency}`, variant: 'error' }); return; }
    payMutation.mutate({ id: bill!.id, payload: { amount, payment_method: payMethod, paid_on: payDate || undefined, reference: payRef.trim() || undefined, ...(isForeign ? { base_amount: baseAmount } : {}) } }, {
      onSuccess: (b) => { setShowPay(false); toast({ title: b.status === 'PAID' ? `${b.bill_number} fully paid` : `Payment recorded — ${formatDocAmount(b.balance_due, currency)} still due`, variant: 'success' }); },
      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
    });
  }
  async function handleAttach(files: File[]) {
    const f = files[0];
    if (!f || !bill) return;
    setUploading(true);
    try {
      const doc = await uploadPurchaseDocument(f);
      docMutation.mutate({ id: bill.id, payload: doc }, {
        onSuccess: () => toast({ title: `${f.name} attached`, variant: 'success' }),
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      });
    } catch (err) {
      toast({ title: err instanceof DocumentUploadError ? err.message : 'Upload failed', variant: 'error' });
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-warning-500 via-orange-500 to-danger-500 p-6 text-white shadow-[0_24px_60px_-24px_rgba(234,88,12,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3"><h1 className="text-2xl font-bold tracking-tight font-mono">{bill.bill_number}</h1><VendorBillStatusBadge status={bill.status} /></div>
            <p className="text-orange-100 mt-1">{bill.supplier_name} · {bill.basis === 'ORDERED' ? 'Full order' : 'Delivered goods'} · against{' '}
              <button type="button" onClick={() => navigate(`/inventory/purchase-orders/${bill.purchase_order_id}`)} className="underline font-mono">{bill.po_number}</button>
              {bill.grn_number && <span> · {bill.grn_number}</span>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums font-mono">{formatDocAmount(bill.balance_due, currency)}</p>
            <p className="text-xs uppercase tracking-wide text-orange-100">Balance due{overdue ? ' · overdue' : ''} · total {formatDocAmount(bill.total_amount, currency)}</p>
          </div>
        </div>
      </div>

      {bill.duplicate_of && bill.status !== 'CANCELLED' && (
        <div role="alert" className="mb-6 flex items-center gap-3 rounded-xl border border-danger-300 dark:border-danger-700 bg-gradient-to-r from-danger-50 to-warning-50 dark:from-danger-900/30 dark:to-warning-900/20 px-5 py-4 shadow-md animate-slide-up">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-warning-500 to-danger-500 text-white shadow"><AlertTriangleIcon className="h-4 w-4" /></div>
          <div>
            <p className="text-sm font-semibold text-danger-900 dark:text-danger-100">
              Vendor invoice {bill.vendor_invoice_number} is already on{' '}
              <button type="button" onClick={() => navigate(`/vendor-bills/${bill.duplicate_of!.id}`)} className="underline hover:no-underline">{bill.duplicate_of.bill_number}</button>.
            </p>
            <p className="text-xs text-danger-800/80 dark:text-danger-200/80">The same supplier invoice may be paid twice. Check both bills before you post or pay.</p>
          </div>
        </div>
      )}

      {isDraft && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/20 px-5 py-4 shadow-md animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow"><EditIcon className="h-4 w-4" /></div>
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Draft — not yet posted. Edit or post it.</p>
              <p className="text-xs text-amber-800/80 dark:text-amber-200/80">Drafts don't count against the purchase order or your payables until posted.</p>
            </div>
          </div>
          {isManager && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => navigate(`/vendor-bills/${bill.id}/edit`)}><EditIcon className="h-4 w-4 mr-1.5" />Edit</Button>
              <Button variant="primary" onClick={() => { setConfirmDuplicate(false); setShowPost(true); }}><CheckIcon className="h-4 w-4 mr-1.5" />Post bill</Button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Bill Information</h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Vendor invoice</dt><dd className="font-medium">{bill.vendor_invoice_number ?? '—'}{bill.vendor_invoice_date ? ` · ${fmtDate(bill.vendor_invoice_date)}` : ''}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Bill date</dt><dd>{fmtDate(bill.bill_date)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Due date</dt><dd className={overdue ? 'text-danger-600 font-medium' : ''}>{fmtDate(bill.due_date)}{bill.supplier_payment_terms ? <span className="text-xs text-neutral-500"> ({bill.supplier_payment_terms.replace('_', ' ')})</span> : null}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Supplier</dt><dd>{bill.supplier_id ? <button type="button" onClick={() => navigate(`/inventory/suppliers/${bill.supplier_id}`)} className="text-teal-600 hover:underline">{bill.supplier_name}</button> : bill.supplier_name}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Location</dt><dd>{bill.location_name ?? '—'}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Created by</dt><dd>{bill.created_by_name ?? '—'} · {fmtDate(bill.created_at)}</dd></div>
              {bill.posted_at && <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Posted</dt><dd>{fmtDate(bill.posted_at)}</dd></div>}
              {bill.notes && <div className="col-span-full"><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Notes</dt><dd>{bill.notes}</dd></div>}
              {bill.cancel_reason && <div className="col-span-full"><dt className="text-xs uppercase tracking-wide text-danger-600 mb-1">Cancelled</dt><dd>{bill.cancel_reason} · {fmtDate(bill.cancelled_at)}</dd></div>}
            </dl>
          </div>

          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Lines ({bill.items.length})</h3>
            <PlaceOfSupply supply={bill.supply} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Item</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Qty</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit cost</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Discount</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Tax</th>
                    <th className="text-right py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.items.map((i) => {
                    const variance = i.po_unit_cost != null ? i.unit_cost - i.po_unit_cost : 0;
                    const ahead = i.po_received_quantity != null && i.billed_elsewhere + i.quantity > i.po_received_quantity;
                    return (
                      <tr key={i.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 align-top">
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-neutral-800 dark:text-neutral-200">{i.item_name}</div>
                          {i.add_to_stock_cost && (
                            <span className="mt-0.5 inline-block rounded-full bg-teal-100 dark:bg-teal-900/30 px-2 py-0.5 text-[11px] font-medium text-teal-800 dark:text-teal-300">Added to stock cost</span>
                          )}
                          {i.po_item_id && (
                            <div className="text-[11px] text-neutral-500 tabular-nums">
                              PO: ordered {i.po_quantity} · received {i.po_received_quantity} · billed elsewhere {i.billed_elsewhere}
                              {ahead && <span className="ml-1 text-warning-700 dark:text-warning-300">· billed ahead of delivery</span>}
                              {variance !== 0 && <span className={`ml-1 ${variance > 0 ? 'text-danger-600' : 'text-success-600'}`}>· {variance > 0 ? '+' : '−'}{formatDocAmount(Math.abs(variance), currency)} vs PO cost</span>}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{i.quantity} <span className="text-xs text-neutral-500">{i.unit ?? ''}</span></td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{formatDocAmount(i.unit_cost, currency)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{i.line_discount > 0 ? `−${formatDocAmount(i.line_discount, currency)}` : '—'}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums"><GstTaxCell supply={bill.supply} gstRate={i.gst_rate} lineTax={i.line_tax} currency={currency} /></td>
                        <td className="py-2.5 text-right font-semibold tabular-nums">{formatDocAmount(i.line_total, currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span className="tabular-nums">{formatDocAmount(bill.subtotal, currency)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span className="tabular-nums text-success-600">{bill.discount_total > 0 ? `−${formatDocAmount(bill.discount_total, currency)}` : '—'}</span></div>
              {isGstSplit(bill.supply) && bill.tax_lines && bill.tax_lines.length > 0
                ? bill.tax_lines.map((l) => (
                  <div key={`${l.kind}-${l.ratePct}`} className="flex justify-between"><span className="text-neutral-500">{l.kind} {l.ratePct}%</span><span className="tabular-nums">{formatDocAmount(l.amount, currency)}</span></div>
                ))
                : <div className="flex justify-between"><span className="text-neutral-500">Tax</span><span className="tabular-nums">{bill.tax_total > 0 ? formatDocAmount(bill.tax_total, currency) : '—'}</span></div>}
              {/* T07: the supplier's round-off, already in the total. */}
              {(bill.round_off ?? 0) !== 0 && <div className="flex justify-between"><span className="text-neutral-500">Round off</span><span className="tabular-nums">{(bill.round_off ?? 0) < 0 ? `−${formatDocAmount(-(bill.round_off ?? 0), currency)}` : formatDocAmount(bill.round_off ?? 0, currency)}</span></div>}
              <div className="flex justify-between font-bold pt-2 border-t border-neutral-100 dark:border-neutral-800"><span>Total</span><span className="tabular-nums">{formatDocAmount(bill.total_amount, currency)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Paid</span><span className="tabular-nums text-success-600">{formatDocAmount(bill.amount_paid, currency)}</span></div>
              {(bill.credited_amount ?? 0) > 0 && <div className="flex justify-between"><span className="text-neutral-500">Debit notes</span><span className="tabular-nums text-violet-600 dark:text-violet-300">−{formatDocAmount(bill.credited_amount ?? 0, currency)}</span></div>}
              <div className="flex justify-between font-semibold"><span>Balance due</span><span className="tabular-nums">{formatDocAmount(bill.balance_due, currency)}</span></div>
            </div>
          </div>

          {(bill.landed_cost_allocations?.length ?? 0) > 0 && (
            <div className={`${GLASS} p-6 animate-fade-in`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-1">Added to stock cost</h3>
              <p className="text-xs text-neutral-500 mb-3">Split by value over the goods on this bill. The part on units already sold shows in the P&amp;L as landed cost on sold stock.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 dark:border-neutral-800">
                      <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Charge → product</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Share</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Into stock</th>
                      <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">On sold stock</th>
                      <th className="text-right py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Cost (WAC)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bill.landed_cost_allocations ?? []).map((a) => (
                      <tr key={`${a.vendor_bill_item_id}-${a.po_item_id}`} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 even:bg-neutral-50 dark:even:bg-neutral-800/40 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all duration-200">
                        <td className="py-2 pr-3">
                          <span className="text-neutral-500">{a.charge_name}</span> → <span className="font-medium text-neutral-800 dark:text-neutral-200">{a.product_name ?? a.product_id}</span>
                          <div className="text-[11px] text-neutral-500 tabular-nums">{a.on_hand_qty} of {a.line_quantity} on hand</div>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{formatDocAmount(a.amount, tenantCurrency)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-teal-700 dark:text-teal-300">{formatDocAmount(a.applied_to_stock, tenantCurrency)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{a.expensed > 0 ? formatDocAmount(a.expensed, tenantCurrency) : '—'}</td>
                        <td className="py-2 text-right tabular-nums">{formatDocAmount(a.cost_before, tenantCurrency)} → <span className="font-semibold">{formatDocAmount(a.cost_after, tenantCurrency)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(bill.debit_notes?.length ?? 0) > 0 && (
            <div className={`${GLASS} p-6 animate-fade-in`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Debit notes ({bill.debit_notes?.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {(bill.debit_notes ?? []).map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span>
                      <span className="font-mono font-semibold text-neutral-900 dark:text-white">{d.number}</span>
                      <span className="text-xs text-neutral-500"> · goods returned on {d.return_number}{d.raised_on_this_bill ? '' : ' (another bill)'}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      {d.applied_here > 0 && <span className="tabular-nums text-violet-700 dark:text-violet-300">−{formatDocAmount(d.applied_here, currency)} on this bill</span>}
                      {d.remaining > 0 && <span className="rounded-full bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 text-xs text-violet-800 dark:text-violet-300">{formatDocAmount(d.remaining, currency)} supplier credit left</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {(bill.debit_notes ?? []).some((d) => d.remaining > 0) && (
                <p className="mt-2 text-xs text-neutral-500">Credit left over is set off against the supplier&apos;s next bill from the supplier page.</p>
              )}
            </div>
          )}

          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Payments ({bill.payments.length})</h3>
            {bill.payments.length === 0 ? <p className="text-sm text-neutral-500">No payments recorded yet.</p> : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {bill.payments.map((p) => (
                  <li key={p.id} className={`flex items-center justify-between gap-2 py-2 text-sm ${p.voided_at ? 'opacity-60' : ''}`}>
                    <span className={`tabular-nums text-neutral-600 dark:text-neutral-400 ${p.voided_at ? 'line-through' : ''}`}>{fmtDate(p.paid_on)}</span>
                    <span className="text-neutral-700 dark:text-neutral-300">{METHOD_OPTIONS.find((m) => m.value === p.payment_method)?.label ?? p.payment_method}{p.reference ? ` · ${p.reference}` : ''}</span>
                    <span className="text-xs text-neutral-500">{p.created_by_name ?? ''}</span>
                    <span className="font-semibold tabular-nums text-right">
                      {formatDocAmount(p.amount, currency)}
                      {isForeign && p.base_amount != null && (
                        <span className="block text-xs font-normal text-neutral-500">
                          paid {formatDocAmount(p.base_amount, tenantCurrency)}
                          {p.fx_gain_loss ? (
                            <span className={p.fx_gain_loss > 0 ? 'text-success-600' : 'text-danger-600'}>
                              {' · '}exchange {p.fx_gain_loss > 0 ? 'gain' : 'loss'} {formatDocAmount(Math.abs(p.fx_gain_loss), tenantCurrency)}
                            </span>
                          ) : null}
                        </span>
                      )}
                      {p.voided_at && (
                        <span className="block text-xs font-normal text-danger-600 dark:text-danger-400">
                          Voided {fmtDate(p.voided_at)}{p.voided_by_name ? ` by ${p.voided_by_name}` : ''}{p.void_reason ? ` · ${p.void_reason}` : ''}
                        </span>
                      )}
                    </span>
                    {isOwner && !p.voided_at && (!isForeign || p.id === latestLivePaymentId) && (
                      <Button variant="ghost" size="sm" className="text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                        onClick={() => { setVoidPaymentId(p.id); setVoidReason(''); }}>Void</Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`${GLASS} p-6`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Documents ({bill.documents.length})</h3>
              {isManager && bill.status !== 'CANCELLED' && (
                <div className="w-48"><FileUpload variant="button" accept={DOCUMENT_ACCEPT} maxSize={DOCUMENT_MAX_BYTES} loading={uploading} onUpload={(files: File[]) => { void handleAttach(files); }} /></div>
              )}
            </div>
            {bill.documents.length === 0 ? <p className="text-sm text-neutral-500">No vendor invoice attached.</p> : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {bill.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between py-2 text-sm gap-3">
                    <div className="min-w-0"><div className="font-medium truncate">{d.file_name ?? d.file_key.split('/').pop()}</div><div className="text-xs text-neutral-500">{d.kind.replace('_', ' ').toLowerCase()} · {formatBytes(d.size_bytes)} · {d.uploaded_by_name ?? ''} · {fmtDate(d.created_at)}</div></div>
                    <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline shrink-0"><DownloadIcon className="h-4 w-4" />View</a>
                  </li>
                ))}
              </ul>
            )}
            {/* POS comments item 11b: documents uploaded on the PO or a receipt
                that this bill does not own still belong with it for reference. */}
            {(bill.related_documents?.length ?? 0) > 0 && (
              <div className="mt-4 border-t border-neutral-100 pt-3 dark:border-neutral-800">
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  From {bill.po_number} ({bill.related_documents!.length})
                </h4>
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {bill.related_documents!.map((d) => (
                    <li key={d.id} className="flex items-center justify-between py-2 text-sm gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.file_name ?? d.file_key.split('/').pop()}</div>
                        <div className="text-xs text-neutral-500">
                          {d.grn_number ?? 'Purchase order'}
                          {d.other_bill_number ? ` · on ${d.other_bill_number}` : ''} · {d.kind.replace('_', ' ').toLowerCase()} · {fmtDate(d.created_at)}
                        </div>
                      </div>
                      <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline shrink-0"><DownloadIcon className="h-4 w-4" />View</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {isManager && (
            <div className={`${GLASS_FORM} p-5`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Actions</h3>
              <div className="space-y-2">
                {isDraft && <Button variant="primary" className="w-full" onClick={() => { setConfirmDuplicate(false); setShowPost(true); }}><CheckIcon className="h-4 w-4 mr-1.5" />Post Bill</Button>}
                {isDraft && <Button variant="secondary" className="w-full" onClick={() => navigate(`/vendor-bills/${bill.id}/edit`)}><EditIcon className="h-4 w-4 mr-1.5" />Edit Draft</Button>}
                {payable && <Button variant="primary" className="w-full" onClick={openPay}><CheckIcon className="h-4 w-4 mr-1.5" />Record Payment</Button>}
                <Button variant="secondary" className="w-full" onClick={handleDownloadPdf} loading={pdfMutation.isPending}><DownloadIcon className="h-4 w-4 mr-1.5" />Download Vendor Bill</Button>
                <Button variant="secondary" className="w-full" onClick={() => navigate(`/inventory/purchase-orders/${bill.purchase_order_id}`)}>Open {bill.po_number}</Button>
                {cancellable && <Button variant="danger" className="w-full" onClick={() => setShowCancel(true)}><BanIcon className="h-4 w-4 mr-1.5" />{isDraft ? 'Discard Draft' : 'Cancel Bill'}</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={showPay} onClose={() => setShowPay(false)} title={`Record payment · ${bill.bill_number}`} allowOverflow
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowPay(false)}>Cancel</Button><Button variant="primary" onClick={handlePay} loading={payMutation.isPending}>Save payment</Button></div>}>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium mb-1">Amount ({docCurrencySymbol(currency)})</label><Input type="number" value={payAmount} onChange={setPayAmount} /><p className="text-xs text-neutral-500 mt-1">Balance due {formatDocAmount(bill.balance_due, currency)}</p></div>
          {isForeign && (
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Paid in {tenantCurrency}</label>
              <Input type="number" value={payBaseShown} onChange={(v) => { setPayBase(v); setPayBaseTouched(true); }} />
              <p className="text-xs text-neutral-500 mt-1">
                What the bank or till actually paid. It starts at the order&apos;s rate; the difference is recorded as an exchange gain or loss.
              </p>
            </div>
          )}
          <div><label className="block text-sm font-medium mb-1">Method</label><Select options={METHOD_OPTIONS} value={payMethod} onChange={(v) => setPayMethod((typeof v === 'string' ? v : 'BANK_TRANSFER') as VendorPaymentMethod)} /></div>
          <div><label className="block text-sm font-medium mb-1">Paid on</label><Input type="date" value={payDate} onChange={setPayDate} /></div>
          <div><label className="block text-sm font-medium mb-1">Reference</label><Input value={payRef} onChange={setPayRef} placeholder="UTR / cheque no." /></div>
          {payMethod === 'CASH' && <p className="col-span-2 text-xs text-warning-700 dark:text-warning-300">Cash payments are counted as cash-out for the {bill.location_name ?? 'PO'} drawer in cash closing.</p>}
        </div>
      </Modal>

      <Modal open={showPost} onClose={() => setShowPost(false)} title={`Post ${bill.bill_number}?`}
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowPost(false)}>Not yet</Button>
          <Button variant="primary" disabled={!!bill.duplicate_of && !confirmDuplicate} loading={postMutation.isPending} onClick={handlePost}><CheckIcon className="h-4 w-4 mr-1.5" />Post bill</Button></div>}>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          Posting makes <span className="font-semibold tabular-nums">{formatDocAmount(bill.total_amount, currency)}</span> payable to {bill.supplier_name} and counts these quantities against {bill.po_number}. You won't be able to edit the lines afterwards.
        </p>
        {bill.duplicate_of && (
          <label className="mt-4 flex items-start gap-2 rounded-lg border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 p-3 text-sm text-danger-900 dark:text-danger-100">
            <input type="checkbox" className="mt-0.5" checked={confirmDuplicate} onChange={(e) => setConfirmDuplicate(e.target.checked)} />
            <span>Vendor invoice {bill.vendor_invoice_number} is already on {bill.duplicate_of.bill_number}. I checked — this is a different invoice. Post anyway.</span>
          </label>
        )}
      </Modal>

      <Modal open={voidPaymentId != null} onClose={() => setVoidPaymentId(null)} title="Void payment"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setVoidPaymentId(null)}>Keep</Button>
          <Button variant="danger" disabled={!voidReason.trim()} loading={voidMutation.isPending} onClick={() => voidPaymentId && voidMutation.mutate({ id: bill.id, paymentId: voidPaymentId, reason: voidReason.trim() }, { onSuccess: () => { setVoidPaymentId(null); toast({ title: 'Payment voided', variant: 'success' }); }, onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}>Void payment</Button></div>}>
        <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">The payment stays on the bill, marked void. The bill's balance and the supplier's payable go back up.{bill.payments.find((p) => p.id === voidPaymentId)?.payment_method === 'CASH' ? ' The cash comes back into the drawer today.' : ''}</p>
        <Input value={voidReason} onChange={setVoidReason} placeholder="Why is this payment being voided?" />
      </Modal>

      <Modal open={showCancel} onClose={() => setShowCancel(false)} title={isDraft ? 'Discard draft' : 'Cancel vendor bill'}
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowCancel(false)}>Keep</Button>
          <Button variant="danger" disabled={!cancelReason.trim()} loading={cancelMutation.isPending} onClick={() => cancelMutation.mutate({ id: bill.id, reason: cancelReason.trim() }, { onSuccess: () => { setShowCancel(false); toast({ title: isDraft ? 'Draft discarded' : 'Vendor bill cancelled', variant: 'success' }); }, onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}>{isDraft ? 'Discard Draft' : 'Cancel Bill'}</Button></div>}>
        <label className="block text-sm font-medium mb-1">Reason</label>
        <Input value={cancelReason} onChange={setCancelReason} placeholder={isDraft ? 'Why is this draft being discarded?' : 'Why is this bill being cancelled?'} />
      </Modal>
    </>
  );
}

export function VendorBillDetailPage() {
  return (
    <PageContainer title="Vendor Bill" breadcrumbs={[{ label: 'Finance' }, { label: 'Vendor Bills', href: '/vendor-bills' }, { label: 'Detail' }]}>
      <ErrorBoundary><VendorBillDetailContent /></ErrorBoundary>
    </PageContainer>
  );
}
